<template>
  <div class="ForeignHealthDurationForm">
    <h2 class="gl_form-title">
      {{ t('health-foreign.duration.legend') }}
    </h2>

    <CardInfoComponent
      v-if="computedDurationAlert"
      :icon="computedDurationAlert.icon"
      textAlign="justify"
    >
      <template v-if="showMediatorContact">
        <p>{{ mediatorDurationAlertText }}</p>
      </template>
      <template v-else>
        {{ mediatorDurationAlertText }}
      </template>
    </CardInfoComponent>

    <DatePickerComponent
      id="init-date"
      :label="t('health-foreign.duration.init-date')"
      :placeholder="t('health-foreign.duration.init-date-placeholder')"
      v-model="initDate"
      :datePickerVisible="true"
      :min-date="minDateInit"
      :max-date="maxDateInit"
      :rules="[required]"
      :tooltip="configurations.tags.startDateTooltip?.label"
    />
    <DatePickerComponent
      id="end-date"
      :label="t('health-foreign.duration.end-date')"
      :placeholder="t('health-foreign.duration.end-date-placeholder')"
      v-model="endDate"
      :datePickerVisible="true"
      :min-date="minDateEnd"
      :max-date="maxDateEnd"
      :rules="[required, validateEndDate]"
      :tooltip="configurations.tags.endDateTooltip?.label"
    />
  </div>
</template>

<script lang="ts" setup>
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useCommonStore } from '@/modules/core/stores'
  import type ForeignHealthConfigurations from '@/modules/foreign-health/common/models/APP/ForeignHealthConfigurations'
  import { addDaysToDate, addMonthsToDate } from '@/utils/date.utils'
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type ForeignHealthRaterAnswers from '../../models/APP/ForeignHealthRaterAnswers'

  const { t } = useI18n()
  const { required } = useRules()
  const commonStore = useCommonStore()

  defineExpose({ triggerSubmit })
  interface PropsInterface {
    nextForm: String[]
    answers: ForeignHealthRaterAnswers
    configurations: ForeignHealthConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const isStudent = computed(
    () => props.answers.stayAbroadType.toString() === props.configurations.insuranceTypes.student.id
  )
  const computedDurationAlert = computed(() => {
    if (hasMediator.value) {
      const mediatorAlert = isStudent.value
        ? props.configurations.tags.durationAlertStudentMediator
        : props.configurations.tags.durationAlertResidentMediator
      if (mediatorAlert) {
        return mediatorAlert
      }
    }

    return isStudent.value
      ? props.configurations.tags.durationAlertStudent
      : props.configurations.tags.durationAlertResident
  })
  const hasMediator = computed(() => !!commonStore.infoMediator)
  const mediatorPhone = computed(() => commonStore.infoMediator?.mediatorPhone || '')
  const showMediatorContact = computed(() => hasMediator.value && !!mediatorPhone.value)
  const mediatorDurationAlertText = computed(() => {
    const alertText = computedDurationAlert.value?.label || ''
    if (!showMediatorContact.value) return alertText
    return alertText.replace(/\{phone\}/g, mediatorPhone.value)
  })

  //Para cambiar la fecha de inicio
  // const minDateInit = computed(() => addDaysToDate(-7))

  const minDateInit = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })

  const maxDateInit = computed(() =>
    props.configurations.maxDays ? addDaysToDate(props.configurations.maxDays) : null
  )

  const initDate = ref(props.answers.initDate ?? null)
  const endDate = ref(props.answers.endDate)

  // TODO: Can this logic be moved to a composable?
  const minDateEnd = computed(() => calculateMaxDate(initDate.value, 'min'))
  const maxDateEnd = computed(() => calculateMaxDate(initDate.value, 'max'))

  function calculateMaxDate(startDate: Date | null, dateOption: 'min' | 'max'): Date {
    let innerStartDate = startDate

    if (!innerStartDate) {
      innerStartDate = new Date()
      innerStartDate.setHours(0, 0, 0, 0)
    }

    if (!props.configurations.hiringEndDateStudent) {
      return innerStartDate
    }
    if (props.configurations.hiringEndDateStudent.time === 'month') {
      return addMonthsToDate(
        Number(props.configurations.hiringEndDateStudent[dateOption]),
        addDaysToDate(-1, innerStartDate)
      )
    }
    return addDaysToDate(
      Number(props.configurations.hiringEndDateStudent[dateOption]),
      innerStartDate
    )
  }

  function stripTime(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  function validateEndDate(value: Date | string | null): true | string {
    if (!initDate.value || !value) {
      return t('rater.health-foreign.duration.min-range-error')
    }

    // Convertir el valor a un formato válido para Date
    const parsedValue = typeof value === 'string' ? value.split('/').reverse().join('-') : value
    const endDateValue = stripTime(
      parsedValue instanceof Date ? parsedValue : new Date(parsedValue)
    )

    if (isNaN(endDateValue.getTime())) {
      return t('rater.health-foreign.duration.min-range-error')
    }

    const minEnd = stripTime(calculateMaxDate(initDate.value, 'min'))
    const maxEnd = stripTime(calculateMaxDate(initDate.value, 'max'))

    if (endDateValue < minEnd) {
      return t('rater.health-foreign.duration.min-range-error')
    }
    if (endDateValue > maxEnd) {
      return t('rater.health-foreign.duration.max-range-error')
    }
    return true
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    const answers = {
      initDate: initDate.value,
      startDate: initDate.value,
      endDate: endDate.value
    }
    return { page, answers }
  }
</script>

<style lang="scss" scoped>
  .gl_form-title {
    margin-bottom: $spacing-xl;
  }
</style>
